import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Grid,
  Box,
  Button,
  TextField,
  InputAdornment,
  Card,
  Typography,
  Avatar,
  Stack,
  Chip,
  MenuItem,
  Select,
  FormControl,
  IconButton,
  Tooltip,
  Collapse,
} from "@mui/material";
import {
  useDeleteCustomerMutation,
  useGetCustomerQuery,
} from "../../../Api/attoDeskApi";
import { ICustomer } from "../../../Api/Interface/api.interface";
import NewPopUpCustomer from "../Edit/index";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import { CiSearch } from "react-icons/ci";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import VisibilitySharpIcon from "@mui/icons-material/VisibilitySharp";
import PrintIcon from "@mui/icons-material/Print";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CustomCheckbox from "../../../Components/Checkbox.tsx";
import Pagination from "../../../Components/Pagination";
import * as XLSX from "xlsx";

const AVATAR_COLORS = [
  "#e53935", "#d81b60", "#8e24aa", "#5e35b1", "#1e88e5",
  "#039be5", "#00897b", "#43a047", "#f4511e", "#fb8c00",
  "#fdd835", "#6d4c41", "#546e7a", "#00acc1", "#3949ab",
];

const getAvatarColor = (name: string): string => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const SOURCES = [
  { value: "", label: "All Sources" },
  { value: "website", label: "Website" },
  { value: "email", label: "Email" },
  { value: "manual", label: "Manual by Admin" },
];

/* ─── Client created-date helpers ──────────────────────────────────────
   Rendered under each client name. The Date constructor converts the
   stored UTC timestamp to the operator's local timezone automatically,
   so the sub-line and tooltip always reflect what the rep would say out
   loud (no raw UTC strings ever reach the DOM).

   formatAddedLabel(iso):
     • Today      → "Added Today, 3:42 PM"
     • Yesterday  → "Added Yesterday, 11:20 AM"
     • Older      → "Added May 17, 2026"

   formatFullTimestamp(iso):
     • Tooltip body → "Sunday, May 17, 2026 at 3:42 PM"

   isNewClient(iso):
     • true when the record is < 24h old (drives the green NEW badge).
   ──────────────────────────────────────────────────────────────────── */
const formatAddedLabel = (iso?: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (!isFinite(d.getTime())) return "";

  const now      = new Date();
  const startOf  = (x: Date) => { const y = new Date(x); y.setHours(0, 0, 0, 0); return y; };
  const dayDiff  = Math.round(
    (startOf(now).getTime() - startOf(d).getTime()) / 86_400_000
  );
  const timePart = d.toLocaleTimeString("en-US", {
    hour: "numeric", minute: "2-digit", hour12: true,
  });

  if (dayDiff === 0) return `Added Today, ${timePart}`;
  if (dayDiff === 1) return `Added Yesterday, ${timePart}`;
  // Older — drop the time so the row stays compact.
  const datePart = d.toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
  return `Added ${datePart}`;
};

const formatFullTimestamp = (iso?: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (!isFinite(d.getTime())) return "";
  const datePart = d.toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });
  const timePart = d.toLocaleTimeString("en-US", {
    hour: "numeric", minute: "2-digit", hour12: true,
  });
  return `${datePart} at ${timePart}`;
};

const isNewClient = (iso?: string | null): boolean => {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (!isFinite(t)) return false;
  return Date.now() - t < 24 * 60 * 60 * 1000;
};

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const LARAVEL_API_URL = process.env.REACT_APP_LARAVEL_API;
  const { showErrorMessage, showMessage } = useNotifier();
  const printRef = useRef<HTMLDivElement>(null);

  // Search
  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [activeDateChip, setActiveDateChip] = useState<string>("");

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  // Dialog state
  const [selectedCoursing, setSelectedCoursing] = useState<ICustomer | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteCategory, setOpenDeleteCategory] = useState(false);
  const [coursingToDelete, setCoursingToDelete] = useState<ICustomer | null>(null);

  const { data, isLoading, isError, refetch } = useGetCustomerQuery({
    searchText: currentSearchQuery,
    id: "",
  });

  useEffect(() => {
    if (debouncedSearchQuery.length >= 4 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
      refetch();
    }
  }, [debouncedSearchQuery]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (coursing: ICustomer) => {
    setSelectedCoursing(coursing);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setSelectedCoursing(null);
    setOpenDialog(false);
  };

  const handleOpenDeletePopup = (coursing: ICustomer) => {
    setCoursingToDelete(coursing);
    setOpenDeleteCategory(true);
  };

  const handleCloseDeletePopup = () => {
    setCoursingToDelete(null);
    setOpenDeleteCategory(false);
  };

  const [deleteCategory] = useDeleteCustomerMutation();
  const handleDelete = async (id: string) => {
    try {
      const response = await deleteCategory(id).unwrap();
      if (response.status) {
        showMessage("Deleted successfully");
        setOpenDeleteCategory(false);
      } else {
        showErrorMessage("Failed to delete the client");
      }
    } catch (error) {
      showErrorMessage("Failed to delete the client");
    }
  };

  // Date quick filters
  const handleDateChip = (chip: string) => {
    const today = new Date();
    if (activeDateChip === chip) {
      setActiveDateChip("");
      setDateFrom("");
      setDateTo("");
      return;
    }
    setActiveDateChip(chip);
    if (chip === "today") {
      const todayStr = today.toISOString().split("T")[0];
      setDateFrom(todayStr);
      setDateTo(todayStr);
    } else if (chip === "this_month") {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateFrom(firstDay.toISOString().split("T")[0]);
      setDateTo(today.toISOString().split("T")[0]);
    }
  };

  const handleDateFromChange = (val: string) => {
    setDateFrom(val);
    setActiveDateChip("");
  };

  const handleDateToChange = (val: string) => {
    setDateTo(val);
    setActiveDateChip("");
  };

  const clearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setSourceFilter("");
    setActiveDateChip("");
    setSearchQuery("");
    setCurrentSearchQuery("");
    setPage(0);
  };

  const hasActiveFilters = dateFrom || dateTo || sourceFilter;

  // Client-side filtering for date range and source
  const allCustomers: ICustomer[] = Array.isArray(data?.data) ? (data?.data as ICustomer[]) : [];

  const filteredCustomers = useMemo(() => {
    let result = allCustomers;

    if (dateFrom) {
      result = result.filter((c: any) => {
        const created = c.created_at ? c.created_at.split("T")[0] : "";
        return created >= dateFrom;
      });
    }
    if (dateTo) {
      result = result.filter((c: any) => {
        const created = c.created_at ? c.created_at.split("T")[0] : "";
        return created <= dateTo;
      });
    }
    if (sourceFilter) {
      result = result.filter((c: any) => {
        const src = (c.customerSource || "manual").toLowerCase();
        return src === sourceFilter.toLowerCase();
      });
    }

    return result;
  }, [allCustomers, dateFrom, dateTo, sourceFilter]);

  // Export to Excel
  const exportToExcel = () => {
    const exportData = filteredCustomers.map((c, i) => ({
      "#": i + 1,
      "First Name": c.customerFirstName || "",
      "Last Name": c.customerLastName || "",
      "Mobile Number": c.customerMobileNum || "",
      "Email": c.customerEmail || "",
      "Company": c.customerCompanyName || "",
      "Street Number": c.customerStreetNumber || "",
      "Street Name": c.customerStreetName || "",
      "City": c.customerCity || "",
      "Province": c.customerProvince || "",
      "Postal Code": c.customerPostalCode || "",
      "Source": c.customerSource || "manual",
      "Created": c.created_at ? new Date(c.created_at).toLocaleDateString("en-CA") : "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Auto-size columns
    const colWidths = Object.keys(exportData[0] || {}).map((key) => ({
      wch: Math.max(key.length, ...exportData.map((row) => String((row as any)[key] || "").length)) + 2,
    }));
    worksheet["!cols"] = colWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Clients");
    XLSX.writeFile(workbook, `Clients_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  // Print
  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Clients List - Durham Convention Centre</title>
          <style>
            body { font-family: 'Segoe UI', Arial, sans-serif; margin: 20px; color: #1a3353; }
            h2 { color: #1a3353; margin-bottom: 4px; }
            .subtitle { color: #666; font-size: 13px; margin-bottom: 16px; }
            table { width: 100%; border-collapse: collapse; font-size: 13px; }
            th { background-color: #1a3353; color: #fff; padding: 10px 8px; text-align: left; font-weight: 600; }
            td { padding: 8px; border-bottom: 1px solid #e0e0e0; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            .footer { margin-top: 20px; font-size: 11px; color: #999; text-align: center; }
            @media print { body { margin: 10px; } }
          </style>
        </head>
        <body>
          <h2>Durham Convention Centre - Clients</h2>
          <div class="subtitle">
            Printed on: ${new Date().toLocaleDateString("en-CA", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
            ${dateFrom || dateTo ? ` | Date Range: ${dateFrom || "Start"} to ${dateTo || "Present"}` : ""}
            ${sourceFilter ? ` | Source: ${SOURCES.find((s) => s.value === sourceFilter)?.label || sourceFilter}` : ""}
            | Total: ${filteredCustomers.length} clients
          </div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Client Name</th>
                <th>Mobile Number</th>
                <th>Email</th>
                <th>Address</th>
                <th>Source</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              ${filteredCustomers.map((c, i) => `
                <tr>
                  <td>${i + 1}</td>
                  <td>${(c.customerFirstName || "") + " " + (c.customerLastName || "")}</td>
                  <td>${c.customerMobileNum || ""}</td>
                  <td>${c.customerEmail || ""}</td>
                  <td>${[c.customerStreetNumber, c.customerStreetName, c.customerCity, c.customerProvince, c.customerPostalCode].filter(Boolean).join(", ")}</td>
                  <td>${c.customerSource || "manual"}</td>
                  <td>${c.created_at ? new Date(c.created_at).toLocaleDateString("en-CA") : ""}</td>
                </tr>
              `).join("")}
            </tbody>
          </table>
          <div class="footer">Durham Convention Centre &copy; ${new Date().getFullYear()}</div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handleAddProductClick = () => {
    navigate("/customer/create");
  };

  if (isLoading)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="10vh">
        <CircularProgress />
      </Box>
    );

  const sourceLabel = (src: string) => {
    const s = (src || "manual").toLowerCase();
    const map: Record<string, { label: string; color: string }> = {
      website: { label: "Website", color: "#3b82f6" },
      email: { label: "Email", color: "#8b5cf6" },
      manual: { label: "Manual", color: "#f59e0b" },
    };
    return map[s] || { label: s, color: "#94a3b8" };
  };

  return (
    <Card className={classes.card}>
      {/* Header */}
      <Grid
        container
        justifyContent={"space-between"}
        spacing={5}
        sx={{ borderBottom: 2, padding: 2 }}
        className={classes.titleGrid}
      >
        <Grid item lg={3} md={3} sm={4} xs={12} pt={2} alignContent={"center"}>
          <Typography className={classes.cardTitle}>All Clients</Typography>
        </Grid>
        <Grid
          item
          lg={9}
          md={9}
          sm={8}
          xs={12}
          sx={{ display: "flex", justifyContent: "flex-end" }}
        >
          <Grid container spacing={1} justifyContent="flex-end" alignItems="center">
            {/* Search */}
            <Grid item>
              <TextField
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CiSearch className={classes.searchWidgetIcon} />
                    </InputAdornment>
                  ),
                  classes: { root: classes.formControlList },
                }}
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Name, Mobile & Email .."
                sx={{ width: "100%" }}
                size="small"
              />
            </Grid>

            {/* Filter Toggle */}
            <Grid item>
              <Tooltip title="Filters">
                <IconButton
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{
                    border: "1px solid",
                    borderColor: hasActiveFilters ? "#c9a84c" : "#e0e0e0",
                    borderRadius: 1,
                    bgcolor: hasActiveFilters ? "#c9a84c10" : "transparent",
                    color: hasActiveFilters ? "#c9a84c" : "#666",
                  }}
                >
                  <FilterListIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>

            {/* Print */}
            <Grid item>
              <Tooltip title="Print Client List">
                <IconButton
                  onClick={handlePrint}
                  sx={{ border: "1px solid #e0e0e0", borderRadius: 1, color: "#666" }}
                >
                  <PrintIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>

            {/* Excel */}
            <Grid item>
              <Tooltip title="Export to Excel">
                <IconButton
                  onClick={exportToExcel}
                  sx={{ border: "1px solid #e0e0e0", borderRadius: 1, color: "#217346" }}
                >
                  <FileDownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>

            {/* Add Customer */}
            <Grid item>
              <Button variant="contained" className={classes.addButton} onClick={handleAddProductClick}>
                Add Client
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Filters Row */}
      <Collapse in={showFilters}>
        <Box sx={{ px: 2, py: 1.5, bgcolor: "#fafbfc", borderBottom: "1px solid #e8e8e8" }}>
          <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1.5}>
            {/* Date Quick Chips */}
            <Stack direction="row" gap={0.5} alignItems="center">
              <CalendarTodayIcon sx={{ fontSize: 16, color: "#94a3b8", mr: 0.5 }} />
              <Chip
                label="Today"
                size="small"
                onClick={() => handleDateChip("today")}
                sx={{
                  fontWeight: 600,
                  cursor: "pointer",
                  bgcolor: activeDateChip === "today" ? "#1a3353" : "#fff",
                  color: activeDateChip === "today" ? "#fff" : "#1a3353",
                  border: "1px solid",
                  borderColor: activeDateChip === "today" ? "#1a3353" : "#d0d0d0",
                  "&:hover": { bgcolor: activeDateChip === "today" ? "#1a3353" : "#f0f0f0" },
                }}
              />
              <Chip
                label="This Month"
                size="small"
                onClick={() => handleDateChip("this_month")}
                sx={{
                  fontWeight: 600,
                  cursor: "pointer",
                  bgcolor: activeDateChip === "this_month" ? "#1a3353" : "#fff",
                  color: activeDateChip === "this_month" ? "#fff" : "#1a3353",
                  border: "1px solid",
                  borderColor: activeDateChip === "this_month" ? "#1a3353" : "#d0d0d0",
                  "&:hover": { bgcolor: activeDateChip === "this_month" ? "#1a3353" : "#f0f0f0" },
                }}
              />
            </Stack>

            {/* Date Range */}
            <TextField
              type="date"
              size="small"
              label="From Date"
              value={dateFrom}
              onChange={(e) => handleDateFromChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160, bgcolor: "#fff", "& .MuiOutlinedInput-root": { fontSize: 13 } }}
            />
            <TextField
              type="date"
              size="small"
              label="To Date"
              value={dateTo}
              onChange={(e) => handleDateToChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160, bgcolor: "#fff", "& .MuiOutlinedInput-root": { fontSize: 13 } }}
            />

            {/* Source Filter */}
            <FormControl size="small" sx={{ minWidth: 160, bgcolor: "#fff" }}>
              <Select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                displayEmpty
                sx={{ fontSize: 13 }}
              >
                {SOURCES.map((s) => (
                  <MenuItem key={s.value} value={s.value} sx={{ fontSize: 13 }}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Chip
                icon={<ClearIcon sx={{ fontSize: 14 }} />}
                label="Clear Filters"
                size="small"
                onClick={clearFilters}
                sx={{
                  fontWeight: 600, cursor: "pointer",
                  bgcolor: "#ef444415", color: "#ef4444",
                  border: "1px solid #ef444430",
                  "&:hover": { bgcolor: "#ef444425" },
                }}
              />
            )}

            {/* Results count */}
            <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
              Showing {filteredCustomers.length} of {allCustomers.length} clients
            </Typography>
          </Stack>
        </Box>
      </Collapse>

      {/* Table */}
      <Grid ref={printRef}>
        <TableContainer>
          <Table className={classes.table}>
            <TableHead className={classes.tableHeaderRow}>
              <TableRow>
                <TableCell className={classes.tablheader}>
                  <CustomCheckbox name="checkbox" />
                </TableCell>
                <TableCell className={classes.tablheader}>
                  <strong>#</strong>
                </TableCell>
                <TableCell className={classes.tablheader}>
                  <strong>Client Name</strong>
                </TableCell>
                <TableCell className={classes.tablheader}>
                  <strong>Mobile Number</strong>
                </TableCell>
                <TableCell className={classes.tablheader}>
                  <strong>E-mail</strong>
                </TableCell>
                <TableCell className={classes.tablheader}>
                  <strong>Source</strong>
                </TableCell>
                <TableCell className={classes.tablheader}>
                  <strong>Bookings</strong>
                </TableCell>
                <TableCell className={classes.tablheader} sx={{ textAlign: "right" }}>
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isError || filteredCustomers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={20} align="center">
                    {isError ? "Error fetching data" : "No data available"}
                  </TableCell>
                </TableRow>
              ) : (
                filteredCustomers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row: ICustomer, index) => {
                    const src = sourceLabel(row.customerSource || "manual");
                    return (
                      <TableRow key={row.id} className={classes.tableRowHover}>
                        <TableCell className={classes.tablCell}>
                          <CustomCheckbox name="checkbox" />
                        </TableCell>
                        <TableCell scope="row" className={classes.tablCell}>
                          {page * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell component="th" scope="row" className={classes.tablCell}>
                          <Grid container spacing={1} alignItems="center">
                            <Grid item>
                              <Box className={classes.SelectImageTableBox}>
                                {row.customerImg ? (
                                  <img
                                    src={`${LARAVEL_API_URL}/${row.customerImg}`}
                                    alt="img"
                                    className={classes.SelectImageTable}
                                  />
                                ) : (
                                  <Avatar
                                    className={classes.SelectImageTable}
                                    sx={{
                                      bgcolor: getAvatarColor(row.customerFirstName || row.customerLastName || "?"),
                                      fontWeight: 700,
                                      fontSize: "1rem",
                                    }}
                                  >
                                    {(row.customerFirstName || row.customerLastName || "?").charAt(0).toUpperCase()}
                                  </Avatar>
                                )}
                              </Box>
                            </Grid>
                            <Grid item>
                              {/* Name + "Added X" sub-line. Stacked vertically
                                  inside the existing cell — no new column. The
                                  sub-line is muted gray to keep the name visually
                                  primary; "NEW" badge surfaces clients added in
                                  the last 24h. */}
                              <Stack spacing={0.2} alignItems="flex-start">
                                <Stack direction="row" alignItems="center" spacing={0.7}>
                                  <Typography sx={{ fontWeight: 600, color: "#1e293b", lineHeight: 1.2 }}>
                                    {(row.customerFirstName || "") + " " + (row.customerLastName || "")}
                                  </Typography>
                                  {isNewClient(row.created_at) && (
                                    <Box
                                      component="span"
                                      sx={{
                                        fontSize: 9,
                                        fontWeight: 800,
                                        letterSpacing: 0.4,
                                        color: "#16a34a",
                                        bgcolor: "#dcfce7",
                                        border: "1px solid #bbf7d0",
                                        borderRadius: "4px",
                                        px: 0.5,
                                        py: 0.05,
                                        lineHeight: 1.2,
                                      }}
                                    >
                                      NEW
                                    </Box>
                                  )}
                                </Stack>
                                {row.created_at && (
                                  <Tooltip title={formatFullTimestamp(row.created_at)} arrow placement="bottom-start">
                                    <Typography
                                      sx={{
                                        fontSize: 10.5,
                                        color: "#94a3b8",
                                        fontWeight: 500,
                                        lineHeight: 1.2,
                                        cursor: "default",
                                      }}
                                    >
                                      {formatAddedLabel(row.created_at)}
                                    </Typography>
                                  </Tooltip>
                                )}
                              </Stack>
                            </Grid>
                          </Grid>
                        </TableCell>
                        <TableCell scope="row" className={classes.tablCell}>
                          {row.customerMobileNum}
                        </TableCell>
                        <TableCell scope="row" className={classes.tablCell}>
                          {row.customerEmail}
                        </TableCell>
                        <TableCell scope="row" className={classes.tablCell}>
                          <Chip
                            label={src.label}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: 11,
                              fontWeight: 600,
                              bgcolor: `${src.color}15`,
                              color: src.color,
                            }}
                          />
                        </TableCell>
                        <TableCell scope="row" className={classes.tablCell}>
                          {row.booking_count ?? 0}
                        </TableCell>
                        <TableCell scope="row" className={classes.tablCell}>
                          <Grid container spacing={1} sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Grid item>
                              <Button
                                variant="contained"
                                className={classes.viewButtonList}
                                onClick={() => navigate(`/customer-history/${row.id}`)}
                              >
                                <VisibilitySharpIcon className={classes.viewListIcon} />
                              </Button>
                            </Grid>
                            <Grid item>
                              <Button
                                variant="contained"
                                className={classes.editButtonList}
                                onClick={() => handleOpenDialog(row)}
                              >
                                <EditSharpIcon className={classes.editListIcon} />
                              </Button>
                            </Grid>
                            <Grid item>
                              <Button
                                variant="contained"
                                className={classes.deleteButtonList}
                                onClick={() => handleOpenDeletePopup(row)}
                              >
                                <DeleteOutlineSharpIcon className={classes.deleteListIcon} />
                              </Button>
                            </Grid>
                          </Grid>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
          <Grid
            spacing={5}
            p={"18px"}
            py={4}
            justifyContent={"end"}
            container
            textAlign={"center"}
            alignContent={"center"}
            alignItems={"center"}
          >
            <Grid
              sx={{
                textAlign: "end",
                justifyContent: "flex-end",
                alignItems: "flex-end",
              }}
            >
              <Pagination
                count={filteredCustomers.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
              />
            </Grid>
          </Grid>
        </TableContainer>
        {selectedCoursing && (
          <NewPopUpCustomer
            openModel={openDialog}
            handleCloseDialog={handleCloseDialog}
            data={selectedCoursing}
          />
        )}
        {coursingToDelete && (
          <DeletePopup
            open={openDeleteCategory}
            handleCloseDelete={handleCloseDeletePopup}
            onConfirm={async () => {
              await handleDelete(coursingToDelete.id.toString());
            }}
            title="Delete Client"
            content={`Are you sure you want to delete "${
              coursingToDelete.customerFirstName + " " + coursingToDelete.customerLastName
            }"?`}
          />
        )}
      </Grid>
    </Card>
  );
};

export default Index;
